import { format } from 'date-fns';
import { CheckCircle2, FolderOpen, CircleDot, MessageSquare } from 'lucide-react';
import type { DayActivity, SubtaskEntry } from '../../lib/summary';

interface DayGroupProps {
  date: Date;
  activities: DayActivity[];
}

export function DayGroup({ date, activities }: DayGroupProps) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-title text-text-primary">{format(date, 'EEEE, MMM d')}</h3>
        <span className="text-label text-text-muted bg-bg-surface px-2.5 py-1 rounded-full border border-border-subtle">
          {activities.length} {activities.length === 1 ? 'task' : 'tasks'}
        </span>
      </div>
      <div className="space-y-3 pl-4">
        {activities.map((act) => {
          const { parentTask, entries, parentCompleted } = act;
          const isProject = parentTask.itemType === 'project';

          const grouped = new Map<string, { task: SubtaskEntry['task']; updates: SubtaskEntry['update'][] }>();
          for (const entry of entries) {
            const existing = grouped.get(entry.task.id);
            if (existing) {
              existing.updates.push(entry.update);
            } else {
              grouped.set(entry.task.id, { task: entry.task, updates: [entry.update] });
            }
          }

          return (
            <div key={parentTask.id} className="surface-card overflow-hidden">
              {/* Parent header */}
              <div className="flex items-center gap-2.5 px-4 py-2.5">
                {parentCompleted ? (
                  <CheckCircle2 size={16} className="text-finish shrink-0" />
                ) : isProject ? (
                  <FolderOpen size={15} style={{ color: parentTask.color ?? '#6366f1' }} />
                ) : (
                  <CircleDot size={15} className="text-accent" />
                )}
                <span className={`text-body font-semibold ${parentCompleted ? 'text-finish' : 'text-text-primary'}`}>
                  {parentTask.title}
                </span>
                {parentCompleted && (
                  <span className="text-label bg-finish/10 text-finish px-2.5 py-0.5 rounded-full ml-auto">
                    Completed
                  </span>
                )}
              </div>

              {/* Subtasks */}
              {grouped.size > 0 && (
                <div className="border-t border-border-subtle">
                  {Array.from(grouped.values()).map(({ task: st, updates }) => (
                    <div key={st.id} className="border-b border-border-subtle last:border-b-0 px-3.5 py-2.5 pl-10">
                      <span className="text-caption font-medium text-text-primary">{st.title}</span>
                      <div className="mt-2 ml-3 space-y-1.5 pl-3">
                        {updates.map((u) => (
                          <div key={u.id} className="flex items-start gap-3">
                            <span className="text-micro text-accent shrink-0 font-medium">
                              {u.progressBefore}% → {u.progressAfter}%
                            </span>
                            {u.note && (
                              <div className="flex items-start gap-1.5">
                                <MessageSquare size={10} className="text-text-muted mt-0.5 shrink-0" />
                                <span className="text-caption text-text-muted whitespace-pre-line">{u.note}</span>
                              </div>
                            )}
                            <span className="text-micro text-text-muted ml-auto shrink-0">
                              {format(new Date(u.createdAt), 'h:mm a')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
